import { useEffect } from 'react';
/**
 * Keeps a ref pointing at the most recent value, without writing to the ref
 * during render. This replaces the common inline idiom that bails a component or
 * hook out of the React Compiler ("Cannot update ref during render"):
 *
 * ```ts
 * const valueRef = useRef(value);
 * valueRef.current = value; // <- ref write during render; compiler bails
 * ```
 *
 * Keep the `useRef` call at the call site — so `react-hooks/exhaustive-deps`
 * still recognises the ref as stable — and hand the ref to `useSyncRef`, which
 * performs the assignment in an effect instead:
 *
 * ```ts
 * const valueRef = useRef(value);
 * useSyncRef(valueRef, value);
 * ```
 *
 * The ref is updated after commit rather than during render, which is safe for
 * the intended use of this pattern: reading the latest value from a once-
 * registered event handler, subscription, or effect that runs after commit. Do
 * NOT use it for a ref whose `current` you read during render (to produce render
 * output) — that is a different problem the compiler flags separately.
 *
 * The sync runs on every render (no dependency array) so `ref.current` always
 * tracks the latest `value`.
 *
 * @param ref The ref (from `useRef`) to keep in sync.
 * @param value The value to write into `ref.current` after every render.
 *
 * @example
 * const onChangeRef = useRef(onChange);
 * useSyncRef(onChangeRef, onChange);
 * useEffect(() => {
 *     const handler = () => onChangeRef.current();
 *     window.addEventListener('resize', handler);
 *     return () => window.removeEventListener('resize', handler);
 * }, []);
 */
export const useSyncRef = (ref, value) => {
    useEffect(() => {
        ref.current = value;
    });
};
